import { useState } from "react";
import { Stack } from "@mui/material";

import classes from "./SkinBoosters.module.css";
import { treatmentPages, servicesData } from "./serviceData";
import ServiceAccordion from "./ServiceAccordion";
import BookingWidgetSection from "../../Components/BookingWidget/BookingWidgetSection";
import useDocumentMeta from "../../utils/useDocumentMeta";
import useSectionView from "../../utils/useSectionView";
import {
  BOOKING_URL,
  trackBookingClick,
  trackFaqToggle,
  trackAccordionExpand,
} from "../../utils/tracking";

import heroImage from "../../Assets/client_treatment_11.jpg";
import glowImage from "../../Assets/client_treatment.png";
import chair_closeup from "../../Assets/chair_closeup.jpg";

const page = treatmentPages.find((p) => p.slug === "skin-boosters");
const services = servicesData.skinBoosters;

const findService = (id) => services.find((s) => s.id === id);

const productCards = [
  {
    icon: "💧",
    label: "Bio-Remodelling",
    name: "Profhilo",
    text: "One of the highest concentrations of hyaluronic acid available, stimulating collagen and elastin for a plumper, firmer complexion. Treats face, neck, decollete, arms, hands and knees.",
    serviceId: "profhilo-face-and-neck",
  },
  {
    icon: "✨",
    label: "Skin Booster",
    name: "Seventy Hyal",
    text: "A bio-stimulating hyaluronic acid injectable that boosts hydration, texture and volume — ideal for tone, laxity, dark circles and sun damage.",
    serviceId: "seventy-hyal-4ml",
  },
];

const faqs = [
  {
    q: "What's the difference between Profhilo and Seventy Hyal?",
    a: "Both are injectable hyaluronic acid skin boosters, but Profhilo has one of the highest concentrations of hyaluronic acid available and is a bio-remodelling treatment recommended as 2 sessions 4 weeks apart. Seventy Hyal is a bio-stimulating booster typically given as a course of 2–3 sessions 14 days apart. We'll recommend the right one for your skin and goals at your consultation.",
  },
  {
    q: "Are skin boosters the same as dermal fillers?",
    a: "No — skin boosters work beneath the surface to improve hydration, tone and overall skin quality, rather than adding volume or shape the way a dermal filler does.",
  },
  {
    q: "Is it painful, and is there any downtime?",
    a: "Most clients tolerate the injections well, with minimal downtime. You may notice some immediate improvement in hydration and glow, with full results developing over the following weeks as collagen production increases.",
  },
  {
    q: "How many sessions will I need?",
    a: "Profhilo is typically given as 2 sessions 4 weeks apart, with a top-up every 6 months. Seventy Hyal is usually recommended as a course of 2–3 sessions, 14 days apart. We'll confirm the right plan for you at your consultation.",
  },
];

const SkinBoostersPage = () => {
  const [expandedService, setExpandedService] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const faqSectionRef = useSectionView("Skin Boosters FAQ");

  useDocumentMeta(
    `${page.title} | Radiance Aesthetics | Hindley | Wigan`,
    page.metaDescription,
  );

  const handleProductClick = (serviceId) => {
    setExpandedService(serviceId);
    const node = document.getElementById(serviceId);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <>
      {/* Hero */}
      <section className={classes.heroSection}>
        <div className={classes.heroContainer}>
          <div className={classes.heroContent}>
            <p className={classes.heroSubtitle}>
              Nurse-Led · Injectable Treatments
            </p>
            <h1 className={classes.heroTitle}>Skin Boosters</h1>
            <p className={classes.heroMeta}>
              <span>From £100</span> · 5 treatments to choose from
            </p>
            <p className={classes.heroDescription}>
              Injectable hyaluronic acid treatments — Profhilo and Seventy Hyal
              — for deep hydration, elasticity and a natural, lasting glow,
              delivered by a qualified nurse.
            </p>
            <a
              href={BOOKING_URL}
              className={classes.heroCta}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackBookingClick("Skin Boosters Hero")}
            >
              Book Now
            </a>
          </div>
          <div className={classes.heroImageWrapper}>
            <img
              className={classes.heroImage}
              src={heroImage}
              alt="Client receiving a skin booster treatment at Radiance Aesthetics Wigan"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Highlight banner */}
      <section className={classes.section}>
        <div className={classes.highlightBannerWrapper}>
          <div className={classes.highlightBanner}>
            <h2 className={classes.highlightTitle}>What Are Skin Boosters?</h2>
            <p className={classes.highlightText}>
              Skin boosters are <strong>injectable hyaluronic acid</strong>{" "}
              treatments that work beneath the skin to boost hydration and
              stimulate collagen and elastin production — different from dermal
              fillers, which add volume and shape. The result is skin that looks
              and feels <strong>plumper, smoother and more radiant</strong>,
              from within.
            </p>
          </div>
        </div>
      </section>

      {/* Choose your treatment */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Choose Your Treatment</p>
          <h2 className={classes.sectionTitle}>Profhilo or Seventy Hyal?</h2>
          <p className={classes.sectionDescription}>
            Two different hyaluronic acid boosters — each card jumps to the full
            details and pricing below.
          </p>
          <div className={classes.finderGrid}>
            {productCards.map((card) => {
              const service = findService(card.serviceId);
              if (!service) return null;
              return (
                <button
                  key={card.serviceId}
                  type="button"
                  className={classes.finderCard}
                  onClick={() => handleProductClick(card.serviceId)}
                >
                  <div className={classes.finderIcon} aria-hidden="true">
                    {card.icon}
                  </div>
                  <p className={classes.finderConcern}>{card.label}</p>
                  <p className={classes.finderFacial}>{card.name}</p>
                  <p className={classes.finderText}>{card.text}</p>
                  <span className={classes.finderLink}>See pricing ↓</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image row */}
      <section className={classes.section}>
        <div className={classes.sectionContainer}>
          <div className={classes.imageRow}>
            <img
              className={classes.rowImage}
              src={glowImage}
              alt="Client's glowing skin after a skin booster treatment at Radiance Aesthetics"
              loading="lazy"
            />
            <img
              className={classes.rowImage}
              src={chair_closeup}
              alt="Client reclining in a treatment chair at Radiance Aesthetics"
              loading="lazy"
              style={{ objectPosition: "center 40%" }}
            />
          </div>
        </div>
      </section>

      {/* Full treatment menu — kept as accordion since pricing varies by area/volume */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Full Price List</p>
          <h2 className={classes.sectionTitle}>Explore Every Treatment</h2>
          <p className={classes.menuIntro}>
            Tap a treatment to see its full description, duration and pricing,
            and book it directly.
          </p>
          <Stack className={classes.accordionWrapper} gap={2}>
            {services.map((service) => (
              <div id={service.id} key={service.id}>
                <ServiceAccordion
                  service={service}
                  isExpanded={expandedService === service.id}
                  onChange={(event, isExpanded) => {
                    if (isExpanded) {
                      trackAccordionExpand(
                        service.name,
                        "Skin Boosters Accordion",
                      );
                    }
                    setExpandedService(isExpanded ? service.id : false);
                  }}
                />
              </div>
            ))}
          </Stack>
        </div>
      </section>

      {/* FAQ */}
      <section className={classes.section} ref={faqSectionRef}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>FAQ</p>
          <h2 className={classes.sectionTitle}>Common Questions</h2>
          <div className={classes.faqContainer}>
            {faqs.map((faq, i) => (
              <div className={classes.faqItem} key={faq.q}>
                <h3
                  className={classes.faqQuestion}
                  onClick={() => {
                    const opening = expandedFaq !== i;
                    trackFaqToggle(faq.q, i, opening);
                    setExpandedFaq(opening ? i : null);
                  }}
                  role="button"
                  aria-expanded={expandedFaq === i}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      const opening = expandedFaq !== i;
                      trackFaqToggle(faq.q, i, opening);
                      setExpandedFaq(opening ? i : null);
                    }
                  }}
                >
                  {faq.q} {expandedFaq === i ? "−" : "+"}
                </h3>
                {expandedFaq === i && (
                  <p className={classes.faqAnswer}>{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={classes.ctaSection} aria-label="Book appointment">
        <h2 className={classes.ctaTitle}>Ready to Boost Your Glow?</h2>
        <p className={classes.ctaDescription}>
          Book online today, or get in touch for a free consultation to find the
          right skin booster for you.
        </p>
        <a
          href={BOOKING_URL}
          className={classes.ctaButton}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackBookingClick("Skin Boosters Bottom CTA")}
        >
          Book Now
        </a>
      </section>

      <div className={classes.ctaWidgetWrapper}>
        <BookingWidgetSection source="Treatments - Skin Boosters" />
      </div>
    </>
  );
};

export default SkinBoostersPage;
